import {
    AnyModel,
    getParent,
    getRootStore,
    ModelClass,
    getSnapshot,
    modelIdKey,
    modelTypeKey,
    SnapshotOutOf,
    model,
} from 'mobx-keystone'
import tr from '/shared/locales'

export type Disposer = () => void

export const $disposers = Symbol('disposers')

export const disposableModel: typeof model = name => clazz => {
    if ('onAttachedToRootStore' in clazz.prototype)
        throw new Error('Don\'t use onAttachedToRootStore!')
    clazz.prototype.onAttachedToRootStore = function onAttachedToRootStore(root: any) {
        this[$disposers] = []
        this.onAttach?.(root)
        return () => {
            disposeAll(this)
        }
    }
    return model('ppldo/' + name)(clazz)
}

export function addDisposer(node: AnyModel, disposer: Disposer) {
    if (!($disposers in node))
        throw new Error('Disposers is not in model!')
    // @ts-ignore
    node[$disposers].push(disposer)
}

export function disposeAll(node: AnyModel) {
    if (!($disposers in node))
        throw new Error('Disposers is not in model!')
    // @ts-ignore
    node[$disposers]?.forEach((d: Disposer) => d())
    // @ts-ignore
    delete node[$disposers]
}

export function isAlive(instance: AnyModel): boolean {
    return !!getRootStore(instance)
}

type ModelInstance<T> = T extends ModelClass<infer M> ? M : never

export function getParentOfType<T extends ModelClass<any>>(node: AnyModel, parentType: T): ModelInstance<T> {
    do {
        const p = getParent(node)
        if (p instanceof parentType)
            return p
        node = p
    } while (node)
    throw new Error('There is no parent of given type!')
}

export type SnapshotMetadata = {
    [modelIdKey]: string
    [modelTypeKey]: string
}

export type CleanSnapshot<T> = Omit<SnapshotOutOf<T>, keyof SnapshotMetadata>

export function getCleanSnapshot<T>(node: T): CleanSnapshot<T> {
    const snap: any = {...getSnapshot(node)}
    delete snap[modelIdKey]
    delete snap[modelTypeKey]
    return snap
}

export function makeColorFromUuid(uuid: string, colorCount: number) {
    const color = uuid.split('-')[0]
    const numberOfColor = parseInt(color, 16)
    return numberOfColor % colorCount
}

export function listen(
    target: EventTarget,
    type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions
) {
    target.addEventListener(type, listener, options)
    return () => target.removeEventListener(type, listener, options)
}

type Key<Keys> = Keys | number | undefined | null | boolean
type KeysArray<Keys> = Array<Key<Keys>>
type KeysDictionary<Keys extends string> = {
    [key in Keys]?: any
}
type Arg<Keys extends string> = Key<Keys> | KeysArray<Keys> | KeysDictionary<Keys>

function _cn(styles: Record<string, string>, names: any) {
    let tmp: string = typeof names
    let out = ''

    if (tmp === 'string' || tmp === 'number') return names ? styles[names] || '' : ''

    if (Array.isArray(names)) {
        for (const n of names)
            if ((tmp = _cn(styles, n)) !== '') out += (out && ' ') + tmp
    } else {
        for (const i of Object.keys(names))
            if (names[i]) out += (out && ' ') + styles[i]
    }

    return out
}

export function cn<S extends { [key: string]: string }>(styles: S, ...args: Arg<Extract<keyof S, string>>[]): string {
    return _cn(styles, args)
}

export function fileDialog(options: { multiple?: boolean, accept?: string } = {}): Promise<FileList> {
    const input = document.createElement('input')
    input.style.position = 'absolute'
    input.style.top = '0px'
    input.style.left = '-1000px'
    document.body.appendChild(input)

    if (options.multiple === true) input.setAttribute('multiple', '')
    if (options.accept !== undefined) input.setAttribute('accept', options.accept)

    input.setAttribute('type', 'file')

    return new Promise(resolve => {
        input.addEventListener('change', () => resolve(input.files!))
        input.addEventListener('click', () => {
            input.focus()
            input.addEventListener('focusout', () => document.body.removeChild(input))
        })

        const evt = document.createEvent('MouseEvents')
        evt.initMouseEvent('click', true, true, window, 1, 0, 0, 0, 0, false, false, false, false, 0, null)
        input.dispatchEvent(evt)
    })
}

export function assertUnreachable(x: never): never {
    throw new Error(`This should be unreachable! but got ${x}`)
}

export function scoreSearch(title: string, search: string): number {
    search = search.toLowerCase()
    const score = Math.min(...title.toLowerCase().split(/\s+/).map(s => {
        const res = s.indexOf(search)
        return res === -1 ? Infinity : res
    }))
    return score === Infinity ? -1 : score
}

export const mentionRE = /@[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}/gi

export type Mention = string

export function findMentions(text: string): Mention[] {
    return text.match(mentionRE) || []
}

export function splitByMentions(text: string): [Mention[], string[]] {
    return [findMentions(text), text.split(mentionRE)]
}

export function replaceMentions<T>(text: string, replacer: (m: Mention, index: number) => T): Array<string | T> {
    const [mentions, texts] = splitByMentions(text)
    const t = mentions.map((m, i) => replacer(m, i))
    for (let i = 0; i < t.length; i++)
        texts.splice((i + 1) * 2 - 1, 0, t[i] as any)
    return texts
}

export function fileListToArray(file_list: DataTransferItemList): DataTransferItem[]
export function fileListToArray(file_list: FileList): File[]
export function fileListToArray(file_list: FileList | DataTransferItemList) {
    const files = []
    for (let i = 0; i < file_list.length; i++)
        files.push(file_list[i])
    return files
}

export class LexoRank {
    static MIN_CHAR: number = LexoRank.byte('a')
    static MAX_CHAR: number = LexoRank.byte('z')

    static new(): string {
        return LexoRank.between('', '')
    }

    static between(prev: string, next: string): string {
        const [res, success] = LexoRank.tryInsert(prev, next)
        if (!success)
            throw new Error('Cannot insert!')
        return res
    }

    static before(next: string): string {
        return LexoRank.between('', next)
    }

    static after(prev: string): string {
        return LexoRank.between(prev, '')
    }

    static tryInsert(prev: string, next: string): [string, boolean] {
        if (prev == '') {
            prev = LexoRank.string(LexoRank.MIN_CHAR)
        }
        if (next == '') {
            next = LexoRank.string(LexoRank.MAX_CHAR)
        }

        let rank = ''
        let i = 0

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const prevChar: number = LexoRank.getChar(prev, i, this.MIN_CHAR)
            const nextChar: number = LexoRank.getChar(next, i, this.MAX_CHAR)

            if (prevChar == nextChar) {
                rank += LexoRank.string(prevChar)
                i++
                continue
            }

            const midChar: number = LexoRank.mid(prevChar, nextChar)
            if (midChar == prevChar || midChar == nextChar) {
                rank += LexoRank.string(prevChar)
                i++
                continue
            }

            rank += LexoRank.string(midChar)
            break
        }

        if (rank >= next) {
            return [prev, false]
        }
        return [rank, true]
    }

    private static string(byte: number): string {
        return String.fromCharCode(byte)
    }

    private static byte(char: string): number {
        return char.charCodeAt(0)
    }

    private static mid(prev: number, next: number): number {
        // TODO: consider to use 8 steps each jump
        return Math.floor((prev + next) / 2)
    }

    private static getChar(s: string, i: number, defaultChar: number): number {
        if (i >= s.length) {
            return defaultChar
        }
        return LexoRank.byte(s.charAt(i))
    }
}

export function resort(list: { rank: string }[], sourceIndex: number | null, targetIndex: number): string {
    if (!list.length)
        return LexoRank.new()

    if (targetIndex >= list.length)
        return LexoRank.after(list[list.length - 1].rank)

    const target = list[targetIndex].rank

    const toUp = sourceIndex === null || (list[sourceIndex].rank) > target

    let newRank
    if (toUp) {
        const prev = list[targetIndex - 1]
        if (prev)
            newRank = LexoRank.between(prev.rank, target)
        else
            newRank = LexoRank.before(target)
    } else {
        const next = list[targetIndex + 1]
        if (next)
            newRank = LexoRank.between(target, next.rank)
        else
            newRank = LexoRank.after(target)
    }
    return newRank
}

export function wrapNewUser(
    {id, profile}: {id: string, profile?: {first_name: string, last_name: string}}
): {id: string, profile: {first_name: string, last_name: string}} {
    if (profile)
        return {id, profile}
    return {id, profile: {first_name: tr.models.newUser, last_name: ''}}
}
